import { useEffect, useRef } from "react";
import { CreateOrderViewModel } from "../../viewmodels/orders/CreateOrderViewModel";

import { connect } from "react-redux";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";

import * as actions from "../../store/actions/index";

import { withRouter } from "../../shared/utility";

import EmptyModal from "../../components/modals/EmptyModal";
import CreateNewOrderForm from "../../components/forms/orders/CreateNewOrderForm";
import BreadCrumbs from "../../components/controls/BreadCrumbs";
import type { AuthState } from "../../types/auth";
import type { CartItem, CartOrderDetails, CartState } from "../../types/orders";

interface CreateOrderProps {
  cart: CartItem[];
  orderDetails: CartOrderDetails | null;
  modifyCart: (item: CartItem) => void;
  setCart: (items: CartItem[]) => void;
  setOrderDetails: (details: CartOrderDetails | null) => void;
  logout: () => void;
}
interface StoreState { auth: AuthState; cart: CartState }

function CreateOrder(props: CreateOrderProps) {
  const { cart, orderDetails, modifyCart, logout, setOrderDetails, setCart } = props;
  const {
    setEmptyModelOpen,
    emptyModal,
    breadCrumbs,
    primaryCategories,
    subCategories,
    products,
    primaryCategorySelected,
    subCategorySelected,
    onProductQuantityChanged,
    openChangeCustomerModal,
    getPrimaryCategoriesForCustomerGroup,
    getCustomersForDropdown,
    revalidateCart,
  } = CreateOrderViewModel(cart, orderDetails, modifyCart, logout, setOrderDetails, setCart);
  const hasLoadedCustomers = useRef(false);
  const loadedCustomerId = useRef<number | null>(null);

  useEffect(() => {
    if (hasLoadedCustomers.current) return;
    hasLoadedCustomers.current = true;
    void getCustomersForDropdown();
  }, [getCustomersForDropdown]);

  useEffect(() => {
    const customerId = orderDetails?.customer_id ?? null;
    if (!customerId || loadedCustomerId.current === customerId) return;
    loadedCustomerId.current = customerId;
    void getPrimaryCategoriesForCustomerGroup(customerId);
    void revalidateCart(customerId);
  }, [getPrimaryCategoriesForCustomerGroup, orderDetails?.customer_id, revalidateCart]);

  var customer_id = 0;
  if (orderDetails) {
    customer_id = Number(orderDetails.customer_id ?? 0);
  }

  return (
    <div className="p-4 sm:px-6 lg:px-8 space-y-4">
      <div className="mt-2 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <h1
            className="text-3xl font-bold tracking-tight apollo-heading"
            style={{ color: "var(--heading-primary)" }}
          >
            {orderDetails?.company_name ? (
              <span>
                <span style={{ color: "var(--nav-text-secondary)" }}>Creating order for </span>
                {orderDetails.company_name}
              </span>
            ) : (
              "Create Order"
            )}
          </h1>
          {customer_id > 0 ? (
            <button
              type="button"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center"
              onClick={openChangeCustomerModal}
            >
              Change Customer
            </button>
          ) : null}
        </div>
        <BreadCrumbs pgs={breadCrumbs} />
        <div className="sm:items-center">
          {customer_id > 0 ? (
            <CreateNewOrderForm
              primaryCategories={primaryCategories}
              subCategories={subCategories}
              products={products}
              primaryCategorySelected={primaryCategorySelected}
              subCategorySelected={subCategorySelected}
              onProductQuantityChanged={onProductQuantityChanged}
            />
          ) : (
            <div className="">
              <p
                className="font-bold text-xl apollo-heading"
                style={{ color: "var(--heading-primary)" }}
              >
                Choose a customer to start order
              </p>
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center mt-3"
                onClick={openChangeCustomerModal}
              >
                Choose Customer
              </button>
            </div>
          )}
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={() => setEmptyModelOpen({ childComponent: null, open: false })}
      />
    </div>
  );
}

const mapStateToProps = (state: StoreState) => {
  return {
    cart: state.cart.cart,
    orderDetails: state.cart.orderDetails,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<StoreState, unknown, AnyAction>) => {
  return {
    modifyCart: (item: CartItem) => dispatch(actions.modifyCart(item)),
    setCart: (newCart: CartItem[]) => dispatch(actions.setCart(newCart)),
    setOrderDetails: (customer: CartOrderDetails | null) => dispatch(actions.setOrderDetails(customer)),
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CreateOrder));
